import { useMemo } from "react";

import type { Person } from "../../api/types";
import { SortableHeader } from "../../components/SortableHeader";
import { UserRefChip } from "../../components/UserRefChip";
import { formatNumber } from "../../lib/format";
import {
  type AchievementSort,
  type AchievementSortKey,
  achievementTotals,
  buildAchievementRows,
  sortAchievementRows
} from "./achievement";

export const defaultAchievementSort: AchievementSort = { key: "pct", dir: "desc" };

// AchievementTable is the Blocks counterpart of the admin People table: one row
// per assignee in the season with their assigned and complete block counts and
// the completion percentage, closed by a totals row. "Achieved" is the server's
// assigned_complete count, so only status=complete counts — pending-approval
// does not. The sort is owned by the page so it survives the table unmounting
// while a season or team switch reloads.
export function AchievementTable({
  people,
  sort,
  onSortChange
}: {
  people: Person[];
  sort: AchievementSort;
  onSortChange: (sort: AchievementSort) => void;
}) {
  const rows = useMemo(() => buildAchievementRows(people), [people]);
  const sorted = useMemo(() => sortAchievementRows(rows, sort), [rows, sort]);
  const totals = useMemo(() => achievementTotals(rows), [rows]);

  if (rows.length === 0) {
    return null;
  }

  function toggleSort(key: AchievementSortKey) {
    if (sort.key === key) {
      onSortChange({ key, dir: sort.dir === "asc" ? "desc" : "asc" });
      return;
    }
    onSortChange({ key, dir: key === "person" ? "asc" : "desc" });
  }

  return (
    <div className="achievement">
      <h3>Achievement</h3>
      <table className="people-table achievement-table">
        <thead>
          <tr>
            <SortableHeader label="Person" sortKey="person" sort={sort} onClick={toggleSort} align="left" />
            <SortableHeader label="Blocks assigned" sortKey="assigned" sort={sort} onClick={toggleSort} align="right" />
            <SortableHeader label="Blocks achieved" sortKey="achieved" sort={sort} onClick={toggleSort} align="right" />
            <SortableHeader label="% achievement" sortKey="pct" sort={sort} onClick={toggleSort} align="right" />
          </tr>
        </thead>
        <tbody>
          {sorted.map((row) => (
            <tr key={row.user.id}>
              <td>
                <UserRefChip user={row.user} />
              </td>
              <td className="people-table__num">{formatNumber(row.assigned)}</td>
              <td className="people-table__num">{formatNumber(row.achieved)}</td>
              <td className="people-table__num">{row.pct}%</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <th scope="row">Total</th>
            <td className="people-table__num">{formatNumber(totals.assigned)}</td>
            <td className="people-table__num">{formatNumber(totals.achieved)}</td>
            <td className="people-table__num">{totals.pct}%</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
